import React, { useState, useEffect } from "react";
import { auth } from "../firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { ShieldCheck, Lock, ArrowRight, Smartphone } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

export default function AuthHelper() {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);

  const query = new URLSearchParams(window.location.search);
  const phone = query.get("phone") || "";
  const targetOrigin = query.get("origin") || "*";

  useEffect(() => {
    if (phone) {
      sendOtp(phone);
    } else {
      toast.error("Phone number is required");
    }
  }, [phone]);

  const sendOtp = async (phoneNumber) => {
    setLoading(true);
    const toastId = toast.loading("Initializing verification...");
    try {
      // Setup Recaptcha
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          'size': 'invisible',
        });
      }
      const appVerifier = window.recaptchaVerifier;
      const formattedNo = phoneNumber.startsWith('+') ? phoneNumber : `+91${phoneNumber}`;

      toast.loading("Sending SMS OTP...", { id: toastId });
      const confirmation = await signInWithPhoneNumber(auth, formattedNo, appVerifier);
      setConfirmationResult(confirmation);
      setOtpSent(true);
      toast.success("OTP sent successfully!", { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to send OTP", { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    setLoading(true);
    const toastId = toast.loading("Verifying OTP...");
    try {
      const result = await confirmationResult.confirm(otp);
      const firebaseToken = await result.user.getIdToken();
      
      toast.success("Authentication successful!", { id: toastId });

      // Post message to parent window and close popup
      if (window.opener) {
        window.opener.postMessage(
          {
            type: "firebase_auth_success",
            token: firebaseToken,
          },
          targetOrigin
        );
        setTimeout(() => window.close(), 1000);
      } else {
        toast.error("Parent window not found. Please log in again.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Invalid verification code. Please try again.", { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      <div id="recaptcha-container"></div>

      <div className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 p-8 space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex p-4 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 shadow-inner">
            {otpSent ? <Lock size={32} /> : <Smartphone size={32} className="animate-pulse" />}
          </div>
          <h2 className="text-2xl font-black text-slate-900 tracking-tight">
            {otpSent ? "Enter Verification Code" : "Sending OTP"}
          </h2>
          <p className="text-slate-500 text-sm font-semibold">
            {otpSent 
              ? `We sent a 6-digit verification code to ${phone}`
              : `Initiating secure verification for ${phone}`}
          </p>
        </div>

        {otpSent ? (
          <form onSubmit={handleVerifyOtp} className="space-y-4">
            <div className="relative">
              <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                maxLength={6}
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="6-digit OTP"
                className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none rounded-xl text-slate-800 text-center font-bold tracking-[0.5em] text-lg shadow-sm transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-black rounded-2xl shadow-xl shadow-indigo-100 transition-all flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-75"
            >
              {loading ? "Verifying..." : "Verify Code"} <ArrowRight size={18} />
            </button>
          </form>
        ) : (
          <div className="flex flex-col items-center justify-center py-6 space-y-3">
            <div className="h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">
              Waiting for Firebase reCAPTCHA...
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
